<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage, getBackendErrorCode } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import { AdminDomainDetails, ProvisionStripeResponse } from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import DnsRecords from '@/components/dashboard/Domains/Dns/DnsRecords.vue'
import DynDnsTokens from '@/components/dashboard/Domains/DynDns/DynDnsTokens.vue'
import { DashboardReasonConfirmDialog } from '@/components/dashboard/common'

defineOptions({ name: 'AdminDomainDetail' })

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const notifyStore = useNotifyStore()

const loading = ref(true)
const domain = ref<AdminDomainDetails | null>(null)
const showAuthCode = ref(false)
const provisionStripeDialog = ref({
  show: false,
  loading: false
})
const mailDeactivateDialog = ref({
  show: false,
  loading: false
})

// The termination behind ending a domain's mail (AGB § 3.2 S. 5 or § 3.5).
// Neither mode is preselected; for cause the customer is told the facts and
// either the prior warning or why none was needed.
const mailTermination = ref({
  mode: '' as '' | 'notice' | 'cause',
  noticeReason: '',
  warningDate: '',
  warningDispensable: false
})

const mailTerminationIncomplete = computed(() => {
  const m = mailTermination.value
  if (m.mode === 'notice') return false
  if (m.mode !== 'cause') return true
  return !m.noticeReason.trim() || (!m.warningDate && !m.warningDispensable)
})

const openMailDeactivate = () => {
  mailTermination.value = { mode: '', noticeReason: '', warningDate: '', warningDispensable: false }
  mailDeactivateDialog.value.show = true
}
const mailReleaseDialog = ref({
  show: false,
  loading: false
})

const domainId = computed(() => Number(route.params.id))

const pageTitle = computed(() => domain.value?.name || 'Domain Details')
const pageSubtitle = computed(() => {
  if (domain.value && domain.value.name !== domain.value.name_ace) {
    return domain.value.name_ace
  }
  return `Domain #${domainId.value}`
})

// Same keys AdminDomainsList filters on, so a status reads identically in the
// list and the detail view.
const statusLabels = computed<Record<string, string>>(() => ({
  active: t('dashboard.admin.domains.status.active'),
  pending_delete: t('dashboard.admin.domains.status.pendingDelete'),
  deleted: t('dashboard.admin.domains.status.deleted'),
  trade_out: t('dashboard.admin.domains.status.tradeOut'),
  external: t('dashboard.admin.domains.status.external'),
  suspended: t('dashboard.admin.domains.status.suspended'),
  transfer_pending: t('dashboard.admin.domains.status.transferPending')
}))

const handleTypeLabels: Record<string, string> = {
  person: 'Person',
  org: 'Organisation',
  role: 'Rolle'
}

async function fetchDomain() {
  loading.value = true
  try {
    const res = await useAxios<null, AdminDomainDetails>(
      'GET',
      `/admin/domain/${domainId.value}`,
      true
    )
    if (res && res.status === STATUS_CODES.OK && res.data) {
      const validate = AdminDomainDetails.safeParse(res.data)
      if (validate.success) {
        domain.value = validate.data
      } else {
        domain.value = res.data as AdminDomainDetails
      }
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminDomainDetail' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: 'Fehler',
      message: getErrorMessage(err as Error)
    })
  } finally {
    loading.value = false
  }
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-'
  return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm')
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'active': return 'status-active'
    case 'pending_delete': return 'status-pending-delete'
    case 'deleted': return 'status-deleted'
    case 'trade_out': return 'status-trade-out'
    case 'external': return 'status-external'
    case 'suspended': return 'status-suspended'
    case 'transfer_pending': return 'status-transfer-pending'
    default: return ''
  }
}

function openProvisionDialog() {
  provisionStripeDialog.value.show = true
}

function closeProvisionDialog() {
  provisionStripeDialog.value.show = false
}

async function confirmProvisionStripe() {
  if (!domain.value?.subscription_id) return

  provisionStripeDialog.value.loading = true

  try {
    const res = await useAxios<null, ProvisionStripeResponse>(
      'POST',
      `/admin/subscription/${domain.value.subscription_id}/provision-stripe`,
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: 'Stripe-Abo erstellt',
        message: `Stripe-Abo ${res.data.stripe_subscription_id} wurde erstellt`
      })
      closeProvisionDialog()
      fetchDomain()
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminDomainDetail:provisionStripe' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: 'Fehler',
      message: getErrorMessage(err as Error)
    })
  } finally {
    provisionStripeDialog.value.loading = false
  }
}

interface AdminMailAction {
  dialog: { show: boolean; loading: boolean }
  endpoint: 'deactivate' | 'release'
  successKey: string
  logPrefix: string
  /**
   * Coded refusals that are an outcome rather than a failure: the action took
   * effect in part (a 502 the hourly job finishes) or had nothing to do (409).
   * Each maps to its message; the dialog closes and the domain reloads.
   */
  outcomes: Record<string, { key: string; alertType: 'info' | 'warning' }>
  /** Coded refusals that leave the dialog open, each with its message. */
  refusals?: Record<string, string>
  /** Fields sent beside the reason. */
  body?: Record<string, unknown>
}

interface AdminMailResponse {
  status?: string
  message?: string
  /** Set by a release when mail stays off, e.g. for lack of entitlement. */
  mail_disabled_reason?: string
}

const STAYS_OFF_KEYS: Record<string, string> = {
  no_entitlement: 'dashboard.admin.domains.mailRelease.staysOffNoEntitlement',
  domain_suspended: 'dashboard.admin.domains.mailRelease.staysOffSuspended'
}

// The admin mail actions post a reason and reload the domain once it is done.
async function runAdminMailAction(action: AdminMailAction, reason: string) {
  action.dialog.loading = true

  try {
    const res = await useAxios<Record<string, unknown>, AdminMailResponse>(
      'POST',
      `/admin/domain/${domainId.value}/mail/${action.endpoint}`,
      true,
      { reason, ...action.body }
    )

    if (res && res.status === STATUS_CODES.OK) {
      const staysOff = res.data?.mail_disabled_reason
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: staysOff ? 'info' : 'success',
        title: t(action.successKey),
        message: staysOff
          ? t(STAYS_OFF_KEYS[staysOff] ?? 'dashboard.admin.domains.mailRelease.staysOff')
          : domain.value?.name ?? ''
      })
      action.dialog.show = false
      fetchDomain()
    }
  } catch (err) {
    const code = getBackendErrorCode(err)
    const outcome = code ? action.outcomes[code] : undefined
    const refusal = code ? action.refusals?.[code] : undefined
    let silentStatuses: number[] = []
    if (outcome) silentStatuses = [STATUS_CODES.CONFLICT]
    else if (refusal) silentStatuses = [STATUS_CODES.BAD_REQUEST, STATUS_CODES.CONFLICT]
    handleAxiosError(err, { logPrefix: action.logPrefix, silentStatuses })
    if (outcome) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: outcome.alertType,
        title: domain.value?.name ?? '',
        message: t(outcome.key)
      })
      action.dialog.show = false
      fetchDomain()
    } else {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'error',
        title: 'Fehler',
        message: refusal ? t(refusal) : getErrorMessage(err as Error)
      })
    }
  } finally {
    action.dialog.loading = false
  }
}

// Notice cancels a plan still billing by term and leaves mail on until then.
// Cause works with or without a mail plan: it ends a plan that is still
// billing today, switches off every mailbox, and holds mail off until it is
// released below.
function confirmMailDeactivate(reason: string) {
  const m = mailTermination.value
  const body: Record<string, unknown> =
    m.mode === 'cause'
      ? {
          mode: 'cause',
          notice_reason: m.noticeReason.trim(),
          ...(m.warningDate ? { warning_date: m.warningDate } : {}),
          warning_dispensable: m.warningDispensable
        }
      : { mode: m.mode }
  const k = (key: string) => `dashboard.admin.domains.mailDeactivate.${key}`
  return runAdminMailAction(
    {
      dialog: mailDeactivateDialog.value,
      endpoint: 'deactivate',
      successKey: k('success'),
      logPrefix: 'AdminDomainDetail:mailDeactivate',
      body,
      outcomes:
        m.mode === 'cause'
          ? {
              // Mail is off and held; ending the paid plan is left to the hourly job.
              stripe_unavailable: { key: k('stripeUnavailable'), alertType: 'warning' }
            }
          : {},
      refusals: {
        mode_required: k('errors.modeRequired'),
        notice_reason_required: k('errors.noticeReasonRequired'),
        warning_required: k('errors.warningRequired'),
        no_mail_plan: k('errors.noMailPlan'),
        subscription_busy: k('errors.busy'),
        // Notice changes nothing when the provider cannot be reached.
        stripe_unavailable: k('errors.noticeStripeUnavailable')
      }
    },
    reason
  )
}

// Lifts an admin hold. Mail is switched back on only when the domain is
// entitled to it; otherwise it stays off as for any unentitled domain.
function confirmMailRelease(reason: string) {
  return runAdminMailAction(
    {
      dialog: mailReleaseDialog.value,
      endpoint: 'release',
      successKey: 'dashboard.admin.domains.mailRelease.success',
      logPrefix: 'AdminDomainDetail:mailRelease',
      outcomes: {
        mail_not_on_hold: { key: 'dashboard.admin.domains.mailRelease.notOnHold', alertType: 'info' },
        // The hold is lifted; the hourly job switches on what is left.
        mail_reenable_failed: { key: 'dashboard.admin.domains.mailRelease.reenableFailed', alertType: 'warning' }
      }
    },
    reason
  )
}

onMounted(() => {
  fetchDomain()
})
</script>

<template>
  <BaseDashboardPage
    :title="pageTitle"
    :subtitle="pageSubtitle"
    icon="mdi-web"
    icon-color="cyan"
    show-back
    :back-route="{ name: 'dashboardAdminDomains' }"
  >
    <template #actions>
      <v-btn
        icon
        variant="text"
        :loading="loading"
        @click="fetchDomain"
      >
        <v-icon icon="mdi-refresh" />
      </v-btn>
    </template>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate size="48" color="primary" />
    </div>

    <!-- Content -->
    <div v-else-if="domain" class="detail-content">
      <!-- Info Cards Row -->
      <div class="info-grid">
        <!-- Domain Info Card -->
        <div class="info-card">
          <h3 class="card-title">Domain Info</h3>
          <div class="info-rows">
            <div class="info-row">
              <span class="info-label">Status</span>
              <span class="status-badge" :class="getStatusClass(domain.status)">
                {{ statusLabels[domain.status] || domain.status }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">Registriert</span>
              <span class="info-value">{{ formatDate(domain.registered) }}</span>
            </div>
            <div v-if="domain.external_id" class="info-row">
              <span class="info-label">Externe ID</span>
              <span class="info-value">#{{ domain.external_id }}</span>
            </div>
            <div v-if="domain.subdomain_count !== undefined" class="info-row">
              <span class="info-label">Subdomains</span>
              <span class="info-value">{{ domain.subdomain_count }}</span>
            </div>
          </div>
        </div>

        <!-- Account Card -->
        <div class="info-card">
          <h3 class="card-title">Account</h3>
          <div class="info-rows">
            <div class="info-row">
              <span class="info-label">E-Mail</span>
              <span class="info-value">{{ domain.account_email }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Account ID</span>
              <span class="info-value">#{{ domain.account_id }}</span>
            </div>
            <div v-if="domain.subscription_id" class="info-row">
              <span class="info-label">Abo ID</span>
              <span class="info-value">#{{ domain.subscription_id }}</span>
            </div>
            <div v-if="domain.subscription_id && !domain.stripe_subscription_id" class="info-row provision-action-row">
              <span class="info-label">Stripe-Abo</span>
              <button class="provision-stripe-btn" @click="openProvisionDialog">
                <v-icon icon="mdi-credit-card-plus" size="16" />
                <span>Erstellen</span>
              </button>
            </div>
            <div v-else-if="domain.stripe_subscription_id" class="info-row">
              <span class="info-label">Stripe-Abo</span>
              <span class="info-value stripe-id">{{ domain.stripe_subscription_id }}</span>
            </div>
          </div>
        </div>

        <!-- Suspension Info (if suspended) -->
        <div v-if="domain.status === 'suspended'" class="info-card suspension-card">
          <h3 class="card-title">Sperrung</h3>
          <div class="info-rows">
            <div class="info-row">
              <span class="info-label">Gesperrt am</span>
              <span class="info-value">{{ formatDate(domain.suspended_at) }}</span>
            </div>
            <div v-if="domain.suspension_reason" class="info-row reason-row">
              <span class="info-label">Grund</span>
              <span class="info-value reason-value">{{ domain.suspension_reason }}</span>
            </div>
          </div>
        </div>

        <!-- DNS/Transfer Info -->
        <div class="info-card">
          <h3 class="card-title">DNS & Transfer</h3>
          <div class="info-rows">
            <div class="info-row">
              <span class="info-label">INWX Nameserver</span>
              <span class="info-value">
                <v-icon
                  :icon="domain.uses_inwx_ns ? 'mdi-check-circle' : 'mdi-close-circle'"
                  :color="domain.uses_inwx_ns ? 'success' : 'warning'"
                  size="18"
                />
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">Transfer-Sperre</span>
              <span class="info-value">
                <v-icon
                  :icon="domain.transfer_lock ? 'mdi-lock' : 'mdi-lock-open'"
                  :color="domain.transfer_lock ? 'success' : 'warning'"
                  size="18"
                />
              </span>
            </div>
            <div v-if="domain.auth_code" class="info-row">
              <span class="info-label">Auth-Code</span>
              <span class="info-value auth-code">
                <code>{{ showAuthCode ? domain.auth_code : '••••••••••••' }}</code>
                <button class="reveal-btn" @click="showAuthCode = !showAuthCode">
                  <v-icon :icon="showAuthCode ? 'mdi-eye-off' : 'mdi-eye'" size="16" />
                </button>
              </span>
            </div>
            <div v-if="domain.nameservers?.length" class="info-row nameservers-row">
              <span class="info-label">Nameserver</span>
              <div class="nameservers-list">
                <code v-for="ns in domain.nameservers" :key="ns">{{ ns }}</code>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Handles Section -->
      <div class="handles-section">
        <h2 class="section-title">Handles</h2>
        <div class="handles-grid">
          <!-- Owner Handle -->
          <div class="handle-card">
            <div class="handle-header">
              <v-icon icon="mdi-account-circle" size="24" />
              <span class="handle-type">Owner (owner-c)</span>
            </div>
            <div v-if="domain.owner_handle" class="handle-content">
              <div class="handle-name">{{ domain.owner_handle.external_name || '-' }}</div>
              <div class="handle-info">
                <span class="handle-id">ID: {{ domain.owner_handle.id }}</span>
                <span v-if="domain.owner_handle.external_id" class="handle-external">
                  Ext: {{ domain.owner_handle.external_id }}
                </span>
                <span v-if="domain.owner_handle.type" class="handle-type-badge">
                  {{ handleTypeLabels[domain.owner_handle.type] || domain.owner_handle.type }}
                </span>
              </div>
            </div>
            <div v-else class="handle-empty">
              Kein Owner-Handle zugewiesen
            </div>
          </div>

          <!-- Admin Handle -->
          <div class="handle-card">
            <div class="handle-header">
              <v-icon icon="mdi-account-cog" size="24" />
              <span class="handle-type">Admin (admin-c)</span>
            </div>
            <div v-if="domain.admin_handle" class="handle-content">
              <div class="handle-name">{{ domain.admin_handle.external_name || '-' }}</div>
              <div class="handle-info">
                <span class="handle-id">ID: {{ domain.admin_handle.id }}</span>
                <span v-if="domain.admin_handle.external_id" class="handle-external">
                  Ext: {{ domain.admin_handle.external_id }}
                </span>
                <span v-if="domain.admin_handle.type" class="handle-type-badge">
                  {{ handleTypeLabels[domain.admin_handle.type] || domain.admin_handle.type }}
                </span>
              </div>
            </div>
            <div v-else class="handle-empty">
              Kein Admin-Handle zugewiesen
            </div>
          </div>
        </div>
      </div>

      <!-- DNS Records Section -->
      <DnsRecords :domain-id="domainId" :domain-name="domain.name" is-admin />

      <!-- DynDNS Section -->
      <DynDnsTokens :domain-id="domainId" :domain-name="domain.name" is-admin />

      <!-- Admin Actions Section -->
      <div class="admin-actions-section">
        <h2 class="section-title">{{ t('dashboard.admin.domains.mailDeactivate.sectionTitle') }}</h2>
        <div class="danger-card">
          <div class="danger-info">
            <span class="danger-title">{{ t('dashboard.admin.domains.mailDeactivate.title') }}</span>
            <span class="danger-description">{{ t('dashboard.admin.domains.mailDeactivate.description') }}</span>
          </div>
          <button class="danger-btn" @click="openMailDeactivate">
            <v-icon icon="mdi-email-off" size="16" />
            <span>{{ t('dashboard.admin.domains.mailDeactivate.button') }}</span>
          </button>
        </div>
        <div class="danger-card release-card">
          <div class="danger-info">
            <span class="danger-title">{{ t('dashboard.admin.domains.mailRelease.title') }}</span>
            <span class="danger-description">{{ t('dashboard.admin.domains.mailRelease.description') }}</span>
          </div>
          <button class="danger-btn release-btn" @click="mailReleaseDialog.show = true">
            <v-icon icon="mdi-lock-open-outline" size="16" />
            <span>{{ t('dashboard.admin.domains.mailRelease.button') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Not Found -->
    <div v-else class="empty-state">
      <v-icon icon="mdi-alert-circle-outline" size="64" />
      <h3>Domain nicht gefunden</h3>
      <p>Die angeforderte Domain konnte nicht gefunden werden.</p>
      <v-btn
        color="primary"
        variant="outlined"
        @click="router.push({ name: 'dashboardAdminDomains' })"
      >
        Zurück zur Liste
      </v-btn>
    </div>

    <!-- Provision Stripe Subscription Dialog -->
    <v-dialog v-model="provisionStripeDialog.show" max-width="420" persistent>
      <div class="provision-dialog">
        <div class="dialog-header">
          <v-icon icon="mdi-credit-card-plus" size="24" class="dialog-icon" />
          <h3>Stripe-Abo erstellen</h3>
        </div>
        <div class="dialog-content">
          <p class="dialog-message">
            Es wird ein Stripe-Abo für diese Domain erstellt. Die Abrechnung beginnt zum nächsten Verlängerungszeitpunkt.
          </p>
          <div v-if="domain" class="domain-info-box">
            <div class="info-row">
              <span class="info-label">Domain:</span>
              <span class="info-value">{{ domain.name }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Abo ID:</span>
              <span class="info-value">#{{ domain.subscription_id }}</span>
            </div>
          </div>
        </div>
        <div class="dialog-actions">
          <button
            class="dialog-btn cancel-btn"
            :disabled="provisionStripeDialog.loading"
            @click="closeProvisionDialog"
          >
            Abbrechen
          </button>
          <button
            class="dialog-btn confirm-btn"
            :disabled="provisionStripeDialog.loading"
            @click="confirmProvisionStripe"
          >
            <v-progress-circular
              v-if="provisionStripeDialog.loading"
              indeterminate
              size="16"
              width="2"
            />
            <template v-else>
              <v-icon icon="mdi-check" size="16" />
              <span>Erstellen</span>
            </template>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Mail Deactivation Dialog -->
    <DashboardReasonConfirmDialog
      v-model="mailDeactivateDialog.show"
      :title="t('dashboard.admin.domains.mailDeactivate.dialogTitle')"
      :subtitle="domain?.name"
      :description="t('dashboard.admin.domains.mailDeactivate.dialogMessage')"
      :warning="mailTermination.mode ? t(`dashboard.admin.domains.mailDeactivate.warning.${mailTermination.mode}`) : undefined"
      icon="mdi-email-off"
      variant="danger"
      :confirm-text="t('dashboard.admin.domains.mailDeactivate.confirm')"
      confirm-icon="mdi-email-off"
      :loading="mailDeactivateDialog.loading"
      :reason-label="t('dashboard.admin.domains.mailDeactivate.reasonLabel')"
      :reason-placeholder="t('dashboard.admin.domains.mailDeactivate.reasonPlaceholder')"
      :confirm-blocked="mailTerminationIncomplete"
      @confirm="confirmMailDeactivate"
    >
      <div class="termination-form">
        <span class="termination-label">{{ t('dashboard.admin.domains.mailDeactivate.modeLabel') }}</span>
        <label v-for="mode in ['notice', 'cause'] as const" :key="mode" class="termination-option">
          <input v-model="mailTermination.mode" type="radio" name="mail-termination-mode" :value="mode" :data-test="`mode-${mode}`" />
          <span>
            <strong>{{ t(`dashboard.admin.domains.mailDeactivate.mode.${mode}`) }}</strong>
            {{ t(`dashboard.admin.domains.mailDeactivate.modeHint.${mode}`) }}
          </span>
        </label>
        <template v-if="mailTermination.mode === 'cause'">
          <p class="termination-note">{{ t('dashboard.admin.domains.mailDeactivate.notForNonPayment') }}</p>
          <v-textarea
            v-model="mailTermination.noticeReason"
            :label="t('dashboard.admin.domains.mailDeactivate.noticeReasonLabel')"
            :hint="t('dashboard.admin.domains.mailDeactivate.noticeReasonHint')"
            persistent-hint
            variant="outlined"
            density="compact"
            rows="3"
            maxlength="1024"
            data-test="notice-reason"
          />
          <v-text-field
            v-model="mailTermination.warningDate"
            type="date"
            :label="t('dashboard.admin.domains.mailDeactivate.warningDateLabel')"
            variant="outlined"
            density="compact"
            data-test="warning-date"
          />
          <label class="termination-option">
            <input v-model="mailTermination.warningDispensable" type="checkbox" data-test="warning-dispensable" />
            <span>{{ t('dashboard.admin.domains.mailDeactivate.warningDispensable') }}</span>
          </label>
        </template>
      </div>
    </DashboardReasonConfirmDialog>

    <DashboardReasonConfirmDialog
      v-model="mailReleaseDialog.show"
      :title="t('dashboard.admin.domains.mailRelease.dialogTitle')"
      :subtitle="domain?.name"
      :description="t('dashboard.admin.domains.mailRelease.dialogMessage')"
      icon="mdi-lock-open-outline"
      variant="primary"
      :confirm-text="t('dashboard.admin.domains.mailRelease.confirm')"
      confirm-icon="mdi-lock-open-outline"
      :loading="mailReleaseDialog.loading"
      :reason-label="t('dashboard.admin.domains.mailRelease.reasonLabel')"
      :reason-placeholder="t('dashboard.admin.domains.mailRelease.reasonPlaceholder')"
      @confirm="confirmMailRelease"
    />
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Loading/Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-state {
  .v-icon {
    color: rgba(239, 83, 80, 0.5);
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 8px;
  }

  p {
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.5);
    margin: 0 0 24px;
  }
}

/* Content */
.detail-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

/* Cards */
.info-card {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 20px;

  &.suspension-card {
    border-color: rgba(239, 83, 80, 0.2);
    background: rgba(239, 83, 80, 0.05);
  }
}

.card-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 16px;
}

.info-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  &.reason-row {
    flex-direction: column;
    align-items: flex-start;
  }
}

.info-label {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
}

.info-value {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: right;

  &.reason-value {
    text-align: left;
    margin-top: 4px;
    padding: 8px 12px;
    background: rgba(239, 83, 80, 0.1);
    border-radius: 6px;
    width: 100%;
  }

  &.auth-code {
    display: flex;
    align-items: center;
    gap: 8px;

    code {
      background: rgba(120, 163, 255, 0.1);
      padding: 4px 8px;
      border-radius: 4px;
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.85rem;
    }
  }
}

.reveal-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.6);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.2);
    color: rgba(200, 218, 255, 0.9);
  }
}

.nameservers-row {
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.nameservers-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;

  code {
    background: rgba(120, 163, 255, 0.1);
    padding: 4px 8px;
    border-radius: 4px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.85rem;
    color: rgba(200, 218, 255, 0.9);
  }
}

/* Status Badges */
.status-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;

  &.status-active {
    background: rgba(76, 175, 80, 0.15);
    color: #4caf50;
  }

  // Amber, not grey: the registration is still live and still the customer's.
  &.status-pending-delete {
    background: rgba(255, 193, 7, 0.15);
    color: #ffc107;
  }

  &.status-deleted {
    background: rgba(158, 158, 158, 0.15);
    color: #9e9e9e;
  }

  &.status-trade-out {
    background: rgba(255, 152, 0, 0.15);
    color: #ffb74d;
  }

  &.status-external {
    background: rgba(33, 150, 243, 0.15);
    color: #64b5f6;
  }

  &.status-suspended {
    background: rgba(239, 83, 80, 0.15);
    color: #ef5350;
  }

  &.status-transfer-pending {
    background: rgba(0, 176, 255, 0.15);
    color: #4fc3f7;
  }
}

/* Handles Section */
.handles-section {
  margin-top: 8px;
}

/* Admin Actions Section */
.admin-actions-section {
  margin-top: 8px;
}

.termination-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.termination-label {
  font-size: 0.85rem;
  font-weight: 600;
}

.termination-option {
  display: flex;
  gap: 8px;
  font-size: 0.85rem;
  line-height: 1.45;
  cursor: pointer;

  input {
    margin-top: 3px;
    flex-shrink: 0;
  }
}

.termination-note {
  margin: 0;
  font-size: 0.85rem;
  color: #ffb74d;
}

.danger-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: rgba(239, 83, 80, 0.05);
  border: 1px solid rgba(239, 83, 80, 0.2);
  border-radius: 12px;
  padding: 20px;
}

.danger-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.danger-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.danger-description {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
}

.danger-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: rgba(239, 83, 80, 0.12);
  border: 1px solid rgba(239, 83, 80, 0.3);
  border-radius: 8px;
  color: #ef5350;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(239, 83, 80, 0.22);
    border-color: rgba(239, 83, 80, 0.45);
  }
}

.release-card {
  margin-top: 12px;
  background: rgba(33, 150, 243, 0.05);
  border-color: rgba(33, 150, 243, 0.2);
}

.release-btn {
  background: rgba(33, 150, 243, 0.12);
  border-color: rgba(33, 150, 243, 0.3);
  color: #64b5f6;

  &:hover {
    background: rgba(33, 150, 243, 0.22);
    border-color: rgba(33, 150, 243, 0.45);
  }
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 16px;
}

.handles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.handle-card {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
}

.handle-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);

  .v-icon {
    color: rgba(0, 176, 255, 0.8);
  }

  .handle-type {
    font-size: 0.9rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
  }
}

.handle-content {
  .handle-name {
    font-size: 1rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.95);
    margin-bottom: 8px;
  }

  .handle-info {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .handle-id,
  .handle-external {
    font-size: 0.8rem;
    color: rgba(200, 218, 255, 0.5);
    padding: 2px 8px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 4px;
  }

  .handle-type-badge {
    font-size: 0.75rem;
    font-weight: 500;
    color: #00B0FF;
    padding: 2px 8px;
    background: rgba(0, 176, 255, 0.1);
    border-radius: 4px;
  }
}

.handle-empty {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.4);
  font-style: italic;
}

/* Light Theme */
.v-theme--lightTheme {
  .info-card,
  .handle-card {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.1);

    &.suspension-card {
      background: rgba(239, 83, 80, 0.03);
    }
  }

  .card-title {
    color: rgba(15, 23, 42, 0.5);
  }

  .info-label {
    color: rgba(15, 23, 42, 0.5);
  }

  .info-value {
    color: rgba(15, 23, 42, 0.9);

    &.auth-code code {
      background: rgba(28, 110, 227, 0.08);
      color: rgba(15, 23, 42, 0.9);
    }
  }

  .nameservers-list code {
    background: rgba(28, 110, 227, 0.08);
    color: rgba(15, 23, 42, 0.9);
  }

  .reveal-btn {
    background: rgba(28, 110, 227, 0.08);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(15, 23, 42, 0.5);

    &:hover {
      background: rgba(28, 110, 227, 0.15);
      color: rgba(15, 23, 42, 0.8);
    }
  }

  .section-title {
    color: rgba(15, 23, 42, 0.9);
  }

  .handle-header {
    border-color: rgba(28, 110, 227, 0.1);

    .handle-type {
      color: rgba(15, 23, 42, 0.9);
    }
  }

  .handle-content {
    .handle-name {
      color: rgba(15, 23, 42, 0.95);
    }

    .handle-id,
    .handle-external {
      color: rgba(15, 23, 42, 0.5);
      background: rgba(0, 0, 0, 0.03);
    }
  }

  .handle-empty {
    color: rgba(15, 23, 42, 0.4);
  }

  .danger-card {
    background: rgba(239, 83, 80, 0.03);
  }

  .release-card {
    background: rgba(33, 150, 243, 0.03);
  }

  .release-btn {
    color: #1976d2;
  }

  .danger-title {
    color: rgba(15, 23, 42, 0.9);
  }

  .danger-description {
    color: rgba(15, 23, 42, 0.5);
  }

  .empty-state {
    h3 {
      color: rgba(15, 23, 42, 0.8);
    }

    p {
      color: rgba(15, 23, 42, 0.5);
    }
  }
}

/* Provision Stripe Button */
.provision-action-row {
  background: rgba(255, 152, 0, 0.05);
  margin: 0 -20px;
  padding: 12px 20px;
  border-radius: 0 0 12px 12px;
}

.provision-stripe-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(255, 152, 0, 0.15);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 6px;
  color: #ffb74d;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 152, 0, 0.25);
    border-color: rgba(255, 152, 0, 0.45);
    transform: translateY(-1px);
  }
}

.stripe-id {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
  background: rgba(103, 58, 183, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
  color: #7c4dff;
}

/* Provision Dialog */
.provision-dialog {
  background: rgba(15, 23, 48, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 16px;
  padding: 24px;
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;

  .dialog-icon {
    color: #ffb74d;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    margin: 0;
  }
}

.dialog-content {
  margin-bottom: 24px;
}

.dialog-message {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.7);
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.domain-info-box {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 10px;
  padding: 12px 16px;

  .info-row {
    display: flex;
    align-items: center;
    gap: 8px;

    &:not(:last-child) {
      margin-bottom: 8px;
    }
  }

  .info-label {
    font-size: 0.8rem;
    color: rgba(200, 218, 255, 0.5);
  }

  .info-value {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
  }
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.dialog-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.cancel-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(200, 218, 255, 0.7);

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
  }
}

.confirm-btn {
  background: rgba(255, 152, 0, 0.2);
  border: 1px solid rgba(255, 152, 0, 0.3);
  color: #ffb74d;

  &:hover:not(:disabled) {
    background: rgba(255, 152, 0, 0.3);
  }
}

/* Mobile */
@media (max-width: 768px) {
  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    &.reason-row {
      flex-direction: column;
    }

    &.provision-action-row {
      flex-direction: row;
      align-items: center;
    }
  }

  .info-value {
    text-align: left;
  }

  .danger-card {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
